import toast from 'react-hot-toast';

import Request from '@/services/util/request-service';
import LocalStorageService from '@/services/storage/general/local-storage-service';
import StorageScopeService from '@/services/storage/general/storage-scope-service';
import { CourseData, CourseId, UserCoursesCookie } from 'course-types';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import ProgressUtilityService from '@/services/course/progress-utility-service';

const COURSE_COOKIE_KEY = 'U_COURSE';
const COURSE_DATA_KEY = 'course-map';

const UserCourseStorageService = {
  getCourseCookie(): UserCoursesCookie | undefined {
    return LocalStorageService.get(StorageScopeService.scopedKey(COURSE_COOKIE_KEY)) as unknown as
      | UserCoursesCookie
      | undefined;
  },

  async retrieveCourseCookie(): Promise<UserCoursesCookie> {
    const userCourses = await (await Request.get(`user/getUserCourse`))?.json();
    if (!userCourses?.userCourses) {
      toast.error('Failed to retrieve user course data');
      return { active: '', all: { user: [], group: {}, enterprise: [] } };
    }

    const data: UserCoursesCookie = { active: '', all: userCourses.userCourses };
    this.setCourseCookie(data);
    return data;
  },

  setCourseCookie(data: UserCoursesCookie): void {
    LocalStorageService.set(StorageScopeService.scopedKey(COURSE_COOKIE_KEY), data);
    LocalStorageService.delete(COURSE_COOKIE_KEY);
    return;
  },

  resetCourseCookie(): void {
    LocalStorageService.delete(StorageScopeService.scopedKey(COURSE_COOKIE_KEY));
    LocalStorageService.delete(COURSE_COOKIE_KEY);
    return;
  },

  getCourseData(): CourseData | undefined {
    return LocalStorageService.get(StorageScopeService.scopedKey(COURSE_DATA_KEY)) as CourseData;
  },

  setCourseDataCookie(courseData: CourseData): void {
    LocalStorageService.set(StorageScopeService.scopedKey(COURSE_DATA_KEY), courseData);
    LocalStorageService.delete(COURSE_DATA_KEY);
  },

  async setCourseData(id: string): Promise<CourseData> {
    const courseData = await this.fetchCourseData(id);
    if (!courseData) {
      throw new Error(`Failed to fetch course data for ${id}`);
    }

    this.setCourseDataCookie(courseData);

    try {
      await UserProgressStorageService.initCourseProgress(courseData);
    } catch (e) {
      console.warn('initCourseProgress failed:', e);
    }

    await ProgressUtilityService.cleanupProgressData(courseData);

    return courseData;
  },

  resetCourseData(): void {
    LocalStorageService.delete(StorageScopeService.scopedKey(COURSE_DATA_KEY));
    LocalStorageService.delete(COURSE_DATA_KEY);
    return;
  },

  async fetchCourseData(id: string): Promise<CourseData | null> {
    try {
      const json = await (await Request.get(`course/getCourse/${id}`))?.json();
      let courseData = (json?.data ?? {}) as CourseData;

      const hasUnits =
        courseData &&
        typeof (courseData as any).units === 'object' &&
        (courseData as any).units !== null;

      if (!hasUnits) {
        courseData = {
          id: id as CourseId,
          name: (courseData as any)?.name ?? 'Error Loading Course',
          image: (courseData as any)?.image ?? null,
          units: {},
        } as CourseData;
      } else {
        (courseData as any).id = id as CourseId;
      }

      return courseData;
    } catch (error) {
      console.error(`Failed to fetch course data for ${id}:`, error);
      return null;
    }
  },
};

export default UserCourseStorageService;
